import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  postStockAdjustment,
  saveStockAdjustment,
} from '@/renderer/services/inventoryStockAdjustmentService';
import type { SaveStockAdjustmentRequest } from '@/renderer/types/inventory';
import { apiError, apiSuccess } from '@/renderer/util/notify';

export function useSaveStockAdjustment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SaveStockAdjustmentRequest) =>
      saveStockAdjustment(request),
    onSuccess: (result, variables) => {
      if (!result.success) {
        apiError(result.error ?? 'Failed to save adjustment');
        return;
      }
      apiSuccess(variables.id ? 'Adjustment updated' : 'Adjustment created');
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-adjustments-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-adjustment-summary'],
      });
    },
    onError: (error: Error) => {
      apiError(error.message ?? 'Failed to save adjustment');
    },
  });
}

export function usePostStockAdjustment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (adjustmentId: string) => postStockAdjustment(adjustmentId),
    onSuccess: (result) => {
      if (!result.success) {
        apiError(result.error ?? 'Failed to post adjustment');
        return;
      }
      const wastage = result.data?.totalWastage ?? 0;
      apiSuccess(
        wastage > 0
          ? `Adjustment posted — wastage of ${wastage.toFixed(2)} recorded`
          : 'Adjustment posted — stock balances updated',
      );
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-adjustments-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-adjustment-summary'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-balances-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-balance-summary'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-movements-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-movement-summary'],
      });
    },
    onError: (error: Error) => {
      apiError(error.message ?? 'Failed to post adjustment');
    },
  });
}
